import type { ArrayViewSort } from '@policyfly/schema/types/schemas/review'
import type { AnyObject, TSFixMe } from '@policyfly/types/common'

interface MatchingIdObject {
  id?: number
  legacyId?: number
  bingoId?: string
  ulid?: string
}

/**
 * Returns true if both objects either have a matching `id`, `legacyId`, `bingoId` or `ulid` field.
 * Missing ids are considered not matching.
 *
 * Useful for comparing objects that could be using any id.
 */
export function hasMatchingId (
  a: MatchingIdObject | null | undefined,
  b: MatchingIdObject | null | undefined,
): boolean {
  if (!a || !b) return false
  if (a.id && b.id && a.id === b.id) return true
  if (a.legacyId && b.legacyId && a.legacyId === b.legacyId) return true
  if (a.bingoId && b.bingoId && a.bingoId === b.bingoId) return true
  if (a.ulid && b.ulid && a.ulid === b.ulid) return true
  return false
}

/**
 * Checks where two arrays have the same values for specified keys at each index.
 * Each array must be the same length to return true.
 *
 * Useful for checking if an array of objects hasn't changed in a computed function to avoid re-rendering.
 *
 * @example
 * const arrayOfValues = computed<Value[]>((oldValue) => {
 *   const newValue = [] // generate new array of values
 *   return equalArrayKeys(newValue, oldValue, ['id'])
 *     ? oldValue
 *     : newValue
 *  })
 */
export function equalArrayKeys<T extends AnyObject> (a: T[], b: T[] | null | undefined, keys: (keyof T)[]): b is T[] {
  if (!b) return false
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const aItem = a[i]!
    const bItem = b[i]!
    for (const key of keys) {
      if (aItem[key] !== bItem[key]) return false
    }
  }
  return true
}

/**
 * Checks whether two objects have the same values for specified keys.
 * If keys are not provided, all keys in the first object will be checked.
 *
 * Useful for checking if an object hasn't changed in a computed function to avoid re-rendering.
 *
 * @example
 * const value = computed<Value>((oldValue) => {
 *   const newValue = {} // generate new value
 *   return equalObjectKeys(newValue, oldValue, ['id'])
 *     ? oldValue
 *     : newValue
 *  })
 */
export function equalObjectKeys<T extends AnyObject> (a: T, b: T | null | undefined, keys?: (keyof T)[]): b is T {
  if (!b) return false
  const keysToCheck = keys || (Object.keys(a) as (keyof T)[])
  for (const key of keysToCheck) {
    if (a[key] !== b[key]) return false
  }
  return true
}

type ArrayViewMappedValue = { v: AnyObject, index: number }

export type ArrayViewSortFunction = ((a: ArrayViewMappedValue, b: ArrayViewMappedValue) => number) | null

/**
 * Generates a sort function for array views based on the provided sort configuration.
 * Function for looking up sort values is provided to allow flexibility in how values are accessed.
 *
 * Used in Review components.
 *
 * @example
 * const sortFunction = sortArrayView(
 *   { path: 'age', reverse: true },
 *   (item, path) => item.v[path].v,
 * )
 */
export function sortArrayView (config: ArrayViewSort | null, getSortValue: (item: ArrayViewMappedValue, path: string) => TSFixMe): ArrayViewSortFunction {
  if (!config) return null
  const { path, reverse } = config
  const direction = reverse ? -1 : 1
  return (a, b) => {
    const aValue = getSortValue(a, path)
    const bValue = getSortValue(b, path)
    if (typeof aValue === 'number' && typeof bValue === 'number') return (aValue - bValue) * direction
    return String(aValue).localeCompare(String(bValue)) * direction
  }
}
